import moment from "moment-timezone/builds/moment-timezone-with-data-10-year-range.js";

export interface OpeningHoursInfo {
  isOpen: boolean;
  openingHours: string;
}

export const isOpen = (
  openCloseString: string,
  { use12HourFormat = false }: { use12HourFormat?: boolean } = {},
): OpeningHoursInfo => {
  // Get current time
  const now: Date = new Date();
  const currentTime: number = +moment.tz(now, moment.tz.guess()).format("HHmm");
  const timeIsIn12HourFormat: boolean =
    openCloseString.toLowerCase().includes("pm") &&
    openCloseString.toLowerCase().includes("am");

  // Extract open and close times from the string
  const times: RegExpMatchArray | null = timeIsIn12HourFormat
    ? openCloseString.match(/\d{1,2}:\d{2} (am|pm)?/gi)
    : openCloseString.match(/\d{1,2}:\d{2}/g);

  if (!times || times.length !== 2) {
    console.error("Invalid open/close string format");
    return { isOpen: false, openingHours: "" };
  }

  // Parse open and close times
  const openTime: number = parseTime(times[0]);
  const closeTime: number = parseTime(times[1]);

  // Determine if current time falls within opening hours
  let isOpen: boolean;
  if (closeTime < openTime) {
    isOpen = currentTime >= openTime || currentTime <= closeTime;
  } else {
    isOpen = currentTime >= openTime && currentTime <= closeTime;
  }

  // Format opening hours
  const openingHours: string =
    formatTime(times[0], use12HourFormat) +
    " - " +
    formatTime(times[1], use12HourFormat);

  return { isOpen, openingHours };
};

const parseTime = (timeString: string): number => {
  const [time, period] = timeString.split(" ");
  // eslint-disable-next-line prefer-const
  let [hour, minute] = time.split(":").map((part) => parseInt(part, 10));

  if (period) {
    if (period === "pm" && hour < 12) {
      hour += 12;
    }
    if (period === "am" && hour === 12) {
      hour = 0;
    }
  }

  return hour * 100 + minute;
};

const formatTime = (timeString: string, use12HourFormat: boolean): string => {
  const [time, period] = timeString.split(" ");
  // eslint-disable-next-line prefer-const
  let [hour, minute] = time.split(":").map((part) => parseInt(part, 10));

  if (use12HourFormat) {
    if (!period) {
      const period: string = hour >= 12 ? "pm" : "am";
      const formattedHour: number =
        hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
      return `${formattedHour}:${minute.toString().padStart(2, "0")} ${period}`;
    } else {
      const formattedHour: number =
        hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
      return `${formattedHour}:${minute.toString().padStart(2, "0")} ${
        period || "am"
      }`; // Default to 'am' if period is not provided
    }
  } else {
    if (period) {
      if (period === "pm" && hour < 12) {
        hour += 12;
      }
      if (period === "am" && hour === 12) {
        hour = 0;
      }
    }
    if (hour === 24) {
      hour = 0;
    }
    return `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
  }
};
